<template>
  <v-card class="rounded-0" :maxWidth="computedMaxCardWidth" :class="{'deleting': deleting, 'not-found': urlStatus && urlStatus === urlStates.broken}" :outlined="!option.shortListed">
    <div class="top-actions">
      <div>
        <v-tooltip v-if="isComparable" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
            v-bind="attrs" v-on="on"
              icon
              small
              light
              :class="{'compare-active': inCompareList}"
              @click="handleToggleCompareAction"
            >
              <v-icon>mdi-compare-horizontal</v-icon>
            </v-btn>
          </template>
          <span>{{inCompareList ? 'Remove from' : 'Add to'}} compare list</span>
        </v-tooltip>
      </div>
      <div v-if="option.url && urlStatus && option.scrapeState !== scrapeStates.scraped">

        <v-progress-circular
          v-if="checkingContent || checkingValues"
          indeterminate
          :size="12"
          :width="1"
        ></v-progress-circular>
        <v-tooltip v-else bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon x-small :color="urlStatus.color" v-bind="attrs" v-on="on" @click="checkOutOfDateContent()">{{ urlStatus.icon }}</v-icon>
          </template>
          <span>{{ urlStatus.name }}</span>
        </v-tooltip>

      </div>
      <div>

        <v-dialog
          v-if="!option.shortListed && option.owner === currentUserID"
          v-model="confirmDeleteDialog"
          persistent
          width="500"
        >
          <template v-slot:activator="{ on, attrs }">

            <v-btn
              icon
              small
              light
              class="delete-action"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon small>mdi-trash-can-outline</v-icon>
            </v-btn>

          </template>

          <v-card>
            <v-card-title class="headline grey lighten-2">
              Delete Option
            </v-card-title>

            <v-card-text v-if="hostname" class="py-4">
                Are you sure you want to delete <strong>{{hostname}} / {{ option.name }}</strong> from your options list?
            </v-card-text>
            <v-card-text v-else class="py-4">
                Are you sure you want to delete <strong>{{ option.name }}</strong> from your options list?
            </v-card-text>

            <v-divider></v-divider>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="info"
                text
                rounded
                :disabled="deleting"
                @click="confirmDeleteDialog = false"
              >
                Cancel
              </v-btn>
              <v-btn
                color="error"
                rounded
                :disabled="deleting"
                :loading="deleting"
                @click="deleteOption()"
              >
                Yes, I am sure
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <div v-else class="non-breaking-space">&nbsp;</div>
      </div>
    </div>
    <v-divider></v-divider>

    <v-img
      :eager="true"
      :src="optionImgSrc"
      class="white--text option-img"
      contain
      :class="imgUrl ? 'align-end' : 'align-center'"
      :gradient="imgUrl ? undefined : 'to bottom, rgba(0,0,0,.5), rgba(0,0,0,.9)'"
      height="200px"
      min-width="248px"
    >
      <!-- <div v-if="isChosenOption && option.url" class="d-flex justify-center mb-6">
        <v-btn rounded color="success" @click="openURL(option.url)">
          Get It Now
        </v-btn>
      </div> -->
      <div v-if="option.scrapeState === scrapeStates.scraped && option.url" class="d-flex justify-center mb-6">
        <div class="mr-2">
          <v-btn small color="success" @click="approveOptionCriteria()">
            Accept
          </v-btn>
        </div>
        <div class="ml-2">
          <v-btn small color="warning" @click="reviewOptionCriteria()">
            Review
          </v-btn>
        </div>
      </div>
      <div v-if="option.url && urlStatus === urlStates.changed && option.scrapeState !== scrapeStates.scraped" class="d-flex justify-center mb-6">
        <div class="ml-2">
          <v-btn small color="warning" @click="checkOutOfDateContent()">
            Review New Values
          </v-btn>
        </div>
      </div>

      <div v-if="option.url && !checkingContent && !checkingValues && urlStatus === urlStates.stale && option.scrapeState === scrapeStates.approved" class="d-flex justify-center mb-6">
        <v-btn small color="warning" @click="checkOutOfDateContent()">
          <div class="d-flex align-items-center flex-column">
          <div class="button-info-text">
            Something changed
          </div>
          <div>
            Re-check values
            </div>
          </div>
        </v-btn>
      </div>

      <v-card-title :class="imgUrl ? 'subtitle-2' : 'text-h5'" class="font-weight-bold flex-column">
        <div>
          <span class="d-option-card-name">
            {{ option.name }}
            <EditOptionNameDialog v-model="option.name" :optionId="option.id" :decisionId="option.decisionId"></EditOptionNameDialog>
          </span>
        </div>
        <v-btn v-if="option.url"
          text
          dark
          x-small
          target="_blank"
          :href="option.url"
        >
          <span v-if="hostname" class="accent--text">{{hostname}}</span><v-icon class="ml-1" color="accent" x-small>mdi-open-in-new</v-icon>
        </v-btn>
      </v-card-title>
    </v-img>

    <v-card-actions>
      <v-btn
        icon
        :color="showProsCons ? 'primary' : reasonColor"
        @click="showProsCons = !showProsCons"
      >
        <v-icon small>mdi-thumbs-up-down</v-icon>
      </v-btn>
      <v-spacer></v-spacer>
      <v-badge v-if="pros.length > 0" icon="mdi-thumb-up" inline left color="success" class="mx-2">
        <span class="text-subtitle2 success--text">{{ pros.length }}</span>
      </v-badge>
      <v-badge v-if="cons.length > 0" icon="mdi-thumb-down" inline color="error" class="mx-2">
        <span class="text-subtitle2 error--text">{{ cons.length }}</span>
      </v-badge>
      <v-spacer></v-spacer>
      <slot name="actions" v-bind:option="option"></slot>
    </v-card-actions>
    <v-expand-transition>
      <div class="criteria-section" v-if="criteria && criteria.length > 0 && !showProsCons">
        <v-divider></v-divider>
        <v-container fluid class="py-0" v-if="editingCriteria">
          <v-row class="mt-6 px-2" v-for="criteriaItem in criteria" v-bind:key="criteriaItem.id">
            <v-select
              v-if="criteriaItem.keywordOnly"
              v-model="editingValues[criteriaItem.id].userValue"
              :items="booleanCriteriaValueOptions"
              :label="`${criteriaItem.name}: ${editingValues[criteriaItem.id].sourceValue ? 'YES' : 'NO'}`"
              placeholder="Provide your own value"
            ></v-select>
            <v-text-field
              v-else
              class="editing-value-field"
              :class="{'with-value': editingValues[criteriaItem.id].sourceValue, 'no-value': (!editingValues[criteriaItem.id].sourceValue && !editingValues[criteriaItem.id].userValue) }"
              :dense="true"
              hide-details="auto"
              :label="`${criteriaItem.name}: ${editingValues[criteriaItem.id].sourceValue ? editingValues[criteriaItem.id].sourceValue : ''}`"
              v-model="editingValues[criteriaItem.id].userValue"
              placeholder="Provide your own value"
            ></v-text-field>
          </v-row>
        </v-container>
        <v-container fluid class="py-0" v-else>
          <div v-for="criteriaItem in criteria" :key="criteriaItem.id">
            <v-row>
              <v-col cols="12" align="start" class="pb-0">
                <span class="info--text font-weight-bold mr-1">{{criteriaItem.name}}:</span>
                <v-progress-circular
                  v-if="checkingContent || checkingValues"
                  indeterminate
                  :size="12"
                  :width="1"
                ></v-progress-circular>
                <span v-else>
                  <!-- <v-tooltip bottom v-if="option.url && option.criteriaValues[criteriaItem.id] && !option.criteriaValues[criteriaItem.id].userValue && !option.criteriaValues[criteriaItem.id].sourceValue">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        x-small
                        v-bind="attrs"
                        v-on="on"
                        @click="checkUnknownValue(criteriaItem.id)"
                      >
                        {{ 'mdi-cloud-search' }}
                      </v-icon>
                    </template>
                    <span>{{valueSyncIcons[criteriaItem.id].tooltip}}</span>
                  </v-tooltip> -->
                  <v-tooltip bottom v-if="option.url && criteriaValues[criteriaItem.id] && !criteriaValues[criteriaItem.id].userValue">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        x-small
                        :color="valueSyncIcons[criteriaItem.id].color"
                        v-bind="attrs"
                        v-on="on"
                        @click="triggerRescrapeOptionValue(criteriaItem.id)"
                      >
                        {{ valueSyncIcons[criteriaItem.id].icon }}
                      </v-icon>
                    </template>
                    <span>{{valueSyncIcons[criteriaItem.id].tooltip}}</span>
                  </v-tooltip>
                  <v-tooltip bottom v-if="option.url && criteriaValues[criteriaItem.id] && criteriaValues[criteriaItem.id].userValue">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        x-small
                        color="info"
                        v-bind="attrs"
                        v-on="on"
                      >
                        mdi-human-edit
                      </v-icon>
                    </template>
                    <span>This value was entered manually</span>
                  </v-tooltip>
                </span>
              </v-col>
              <v-col cols="12" align="start" class="pt-0">
                <CriteriaValue v-if="option.criteriaValues" :keywordOnly="criteriaItem.keywordOnly" :valueExpired="outOfDateCriteria[criteriaItem.id]" :value="criteriaValues[criteriaItem.id]" :range="criteriaItem.range" />
              </v-col>
            </v-row>
         </div>
        </v-container>
        <div class="d-flex justify-space-between" v-if="option.owner === currentUserID && isEditable">
          <div v-if="editingCriteria">
            <v-btn
              text
              small
              @click="cancelEditCriteria"
            >
              Cancel
            </v-btn>
            <v-btn
              color="primary"
              text
              small
              @click="saveCriteria"
            >
              Save
            </v-btn>
          </div>
          <v-btn
            class="mx-3"
            v-else
            icon
            small
            :disabled="option.scrapeState === scrapeStates.scraped || checkingValues || checkingContent"
            @click="editCriteria"
          >
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <div>
            <v-tooltip top v-if="!editingCriteria && option.url && option.scrapeState !== scrapeStates.scraped && urlStatus !== urlStates.broken">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  class="mx-3"
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  :loading="rebuilding"
                  :disabled="rebuilding || checkingValues || checkingContent"
                  @click="reBuildOptionValues"
                >
                  <v-icon>mdi-cloud-refresh</v-icon>
                </v-btn>
              </template>
              <span>Completely rebuild this option from the source URL</span>
            </v-tooltip>
          </div>
        </div>
      </div>
    </v-expand-transition>
    <v-expand-transition>
      <div v-if="showProsCons">
        <v-divider></v-divider>
        <v-card flat>
        <v-container fluid>
          <v-row>
            <v-col>
              <div class="d-flex align-center justify-space-between text-subtitle-1 text-center">
                <div>
                  <strong v-if="pros.length > 0">{{ pros.length }}</strong> Pros
                </div>
                <OptionReasonDialog :isContraReason="false" @onSave="handleNewReason" />
              </div>
              <div>
                <v-chip class="ma-2"
                  :close="pro.owner === currentUserID"
                  color="success"
                  text-color="white"
                  close-icon="mdi-delete"
                  @click:close="deleteReason(pro)"
                  v-for="pro in pros"
                  :key="pro.id"
                >
                  <UserAvatar
                    class="mr-2"
                    :email="pro.ownerName"
                    :size="24"
                    color="green darken-4"
                  />
                    {{ pro.reasonText }}
                </v-chip>
              </div>
            </v-col>
            <v-divider vertical />
            <v-col>
              <div class="d-flex align-center justify-space-between text-subtitle-1 text-center">
                <div>
                  <strong v-if="cons.length > 0">{{ cons.length }}</strong> Cons
                </div>
                <OptionReasonDialog :isContraReason="true" @onSave="handleNewReason" />
              </div>
              <div>
                <v-chip class="ma-2"
                  :close="con.owner === currentUserID"
                  color="error"
                  text-color="white"
                  close-icon="mdi-delete"
                  @click:close="deleteReason(con)"
                  v-for="con in cons"
                  :key="con.id"
                >
                  <UserAvatar
                    class="mr-2"
                    :email="con.ownerName"
                    :size="24"
                    color="red darken-4"
                  />
                    {{ con.reasonText }}
                </v-chip>
              </div>
            </v-col>
          </v-row>
        </v-container>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            rounded
            small
            @click="showProsCons = false"
          >
            Done
          </v-btn>
        </v-card-actions>
      </v-card>
      </div>
    </v-expand-transition>
  </v-card>
</template>

<script>
import Constants from '../constants.json'
// This import loads the firebase namespace along with all its type information.
import * as firebase from 'firebase/app'
// Load the auth feature into the firebase namespace.
import 'firebase/auth'
import axios from 'axios'

import CriteriaValue from '@/components/CriteriaValue.vue'
import OptionReasonDialog from '@/components/OptionReasonDialog.vue'
import guid from '@/mixins/DecidedlyGUID.js'
import UserAvatar from '@/components/UserAvatar.vue'
import EditOptionNameDialog from '@/components/EditOptionNameDialog.vue'

// Veux types
import * as DecisionTypes from '../store/types/decision'

export default {
  name: 'OptionCard',
  mixins: [guid],
  components: {
    CriteriaValue,
    OptionReasonDialog,
    UserAvatar,
    EditOptionNameDialog
  },
  props: {
    option: Object,
    imgUrl: String,
    criteria: Array,
    maxCardWidth: {
      type: Number,
      default: 350
    },
    isNewOption: {
      type: Boolean,
      default: false
    },
    isChosenOption: {
      type: Boolean,
      default: false
    },
    isComparable: {
      type: Boolean,
      default: true
    },
    inCompareList: {
      type: Boolean,
      default: false
    },
    isEditable: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      showProsCons: false,
      editingCriteria: false,
      editingValues: null,
      deleting: false,
      // checkingContent: false,
      checkingValuesImmediate: false,
      checkingContentTimestamp: 0,
      confirmDeleteDialog: false,
      optionReasons: [],
      urlStates: {
        unknown: {name: 'Link status unknown', icon: 'mdi-link-off', color: 'info'},
        broken: {name: 'Broken Link. The page for this option is no longer available', icon: 'mdi-link-off', color: 'error'},
        latest: {name: 'This option is up to date', icon: 'mdi-clock-check-outline', color: 'success'},
        stale: {name: 'The page has changed and your values could be out of date. Click to refresh and check the values are up to date', icon: 'mdi-refresh', color: 'warning'},
        changed: {name: 'The values selected from this page are out of date. Click to fetch the latest content for this option and choose new values', icon: 'mdi-swap-horizontal', color: 'warning'}
      },
      // lastScrapeStatus: null,
      // checkingValues: false,
      //outOfDateCriteria: {},
      updatingSha256: false,
      checkedValuesScrapeResult: null,
      // checkContentChangedInterval: null, // Interval for waiting for the checkURL job to finish
      // checkRescrapeFinishedInterval: null, // Interval for waiting for the rescrape job to finish
      // checkScrapeFinishedInterval: null, // Interval for waiting for the single value scrape job to finish
      rebuilding: false,
      booleanCriteriaValueOptions: ['YES', 'NO']
    }
  },
  computed: {
    computedLastCheckTime () {
      return this.option.lastCheckTime || 0
    },
    checkingContent () {
      let res = false
      if (!this.isNewOption && this.option.scrapeState === Constants.SCRAPE_STATES.approved) {
        // const contentStateCheckedAt = this.option.contentState ? this.option.contentState.checkedAt : 0;
        // return this.computedLastCheckTime < contentStateCheckedAt
        const timeSinceListCheck = Math.abs(this.computedLastCheckTime - Date.now())
        // console.log(`Checking if it is time to check. computedLastCheckTime=${this.computedLastCheckTime} ${timeSinceListCheck}`)
        const timeToCheck = timeSinceListCheck > 7200000 /* 2 hours in milliseconds*/
        if (this.option.url && timeToCheck) {
          res = true
        }
      }
      // console.log(`Checking content = ${res}`)
      return res
    },
    checkingValues () {
      return this.checkingValuesImmediate || this.option.scrapeState === Constants.SCRAPE_STATES.scraping || this.option.scrapeState === Constants.SCRAPE_STATES.validating
    },
    scrapeStates () {
      return Constants.SCRAPE_STATES
    },
    lastScrapeStatus () {
      let res = this.urlStates.unknown
      if (this.isNewOption) {
        res = this.urlStates.latest
      }
      else if (this.option.contentState) {

        if (this.option.contentState.broken) {
          res = this.urlStates.broken
        }
        else if (this.option.rescrapeResult && this.option.rescrapeResult.newValues && Object.keys(this.option.rescrapeResult.newValues).length > 0) {
          res = this.urlStates.changed
        }
        else if (this.option.contentState.sha256 ) {
          if (this.option.contentState.sha256 === this.option.sha256) {
            res = this.urlStates.latest
          } else {
            res = this.urlStates.stale
          }
        }
      }
      return res
    },
    computedMaxCardWidth () {
      switch (this.$vuetify.breakpoint.name) {
        case 'xs': return '100%'
        case 'sm': return '100%'
        default: return this.maxCardWidth
      }
    },
    currentUserID () {
      return firebase.auth().currentUser.uid
    },
    optionImgSrc () {
      let res = this.imgUrl
      if (!res) {
        res = `${Constants.SCREENSHOT_CDN}/unknownimage.png`
      }
      // if (res.indexOf('https:') !== 0) {
      //    res = `${Constants.SCREENSHOT_CDN}/notsecureimage.png`
      // }
      return res
    },
    outOfDateCriteria () {
      let res = {}

      this.criteria.forEach((nextCriterion) => {
        let newValues = []
        // if (this.checkedValuesScrapeResult) {
        if (this.option.rescrapeResult && this.option.rescrapeResult.newValues) {
          // newValues = this.checkedValuesScrapeResult.newValues[nextCriterion.id] || null
          newValues = this.option.rescrapeResult.newValues[nextCriterion.id] || null
        }
        // Set the value to the first in the list
        if (newValues && newValues.length > 0) {
          if (this.criteriaValues[nextCriterion.id]) {
            const sourceVal = this.criteriaValues[nextCriterion.id].sourceValue
            const foundValue = newValues.find((nextItem) => {
              return nextItem.value === sourceVal
            })
            if (!foundValue) {
              res[nextCriterion.id] = true
            }
          }
        }
      })

      return res
    },
    hostname () {
      if (this.option.url) {
        return new URL(this.option.url).hostname
      } else {
        return null
      }
    },
    urlStatus () {
      let res = this.lastScrapeStatus || this.urlStates.latest
      // Check if the status is changed, but there are no out of date criteria, in this case we want to set the status to latest
      if (res === this.urlStates.changed && (Object.keys(this.outOfDateCriteria).length === 0 && this.outOfDateCriteria.constructor === Object)) {
        res = this.urlStates.latest
      }

      return res
    },
    valueSyncIcons () {
      const valueIconsMap = {}
      this.criteria.forEach((item) => {
        let iconStr = ''
        let iconColor = 'success'
        let tooltipText  = ``
        let valueStatus = this.urlStates.latest
        if (item.ignoreInScrape) {
          iconStr = 'mdi-human-edit'
          iconColor = 'info'
          tooltipText = `${item.name} has been excluded from the search`
        }
        else if (this.option.scrapeState === Constants.SCRAPE_STATES.scraped) {
          iconStr = 'mdi-help'
          iconColor = 'info'
          tooltipText = `This value has not been approved yet`
        }
        else if (this.urlStatus === this.urlStates.stale) {
          iconStr = 'mdi-help'
          iconColor = 'info'
          tooltipText = `This value might be out of date as the content on ${this.hostname} has changed`
          valueStatus = this.urlStates.stale
        }
        else if (this.urlStatus === this.urlStates.broken) {
          iconStr = 'mdi-help'
          iconColor = 'info'
          tooltipText = `This value might be out of date as the link to ${this.hostname} is not currently available`
          valueStatus = this.urlStates.stale
        }
        else if (this.option.scrapeState === Constants.SCRAPE_STATES.approved) {
          if (this.option.url && (!this.criteriaValues[item.id] || !this.criteriaValues[item.id].sourceValue) ) {
            iconStr = 'mdi-cloud-search'
            iconColor = 'info'
            tooltipText = `No value found or provided for ${item.name} yet. Click to search ${this.hostname}.`
            valueStatus = 'unscraped'
          }
          else if (this.outOfDateCriteria[item.id]) {
            iconStr = 'mdi-swap-horizontal'
            iconColor = 'warning'
            tooltipText = `This value has change on ${this.hostname}`
            valueStatus = this.urlStates.changed
          } else {
            iconStr = 'mdi-clock-check-outline'
            iconColor = 'success'
            tooltipText  = `This value is up to date and found on ${this.hostname}`
            valueStatus = this.urlStates.latest
          }
        }
        valueIconsMap[item.id] = { icon: iconStr, color: iconColor, tooltip: tooltipText, status: valueStatus}
      })
      return valueIconsMap
    },
    reasonColor () {
      let colorValue = 'info'
      if (this.optionReasons.length > 0) {
        if (this.pros.length > this.cons.length) {
          colorValue = 'success'
        } else if (this.pros.length < this.cons.length) {
          colorValue = 'error'
        }
      }
      return colorValue
    },
    pros () {
      return this.optionReasons.filter(reason => reason.isContra === false)
    },
    cons () {
      return this.optionReasons.filter(reason => reason.isContra === true)
    },
    criteriaValues () {
      let res = {...this.option.criteriaValues}
      this.criteria.forEach((item) => {
        if (!this.option.criteriaValues[item.id]) {
          res[item.id] = {userValue: null, sourceValue: null}
        }
      })

      return res
    }
  },
  mounted: function () {
    // this.updateCriteriaValues()

    // TODO if this option state is validating, then check if we need to start checking if validation is finished.
    // if (this.option.scrapeState === Constants.SCRAPE_STATES.validating && !this.checkRescrapeFinishedInterval) {
    //   this.startCheckRescrapingFinished()
    // } else if (this.isNewOption) {
    //   // this.lastScrapeStatus = this.urlStates.latest
    //   if (!this.option.url) {
    //     // New option with no URL to pull the values from, so open it for editing
    //     this.editCriteria()
    //   }
    // } else { // Not a new option, so check the URL to update status
    //   if (this.option.contentState) {
    //     this.$store.commit(`decision/${DecisionTypes.MUTATION_UPDATE_OPTION_CHECKED_TIME}`, {id: this.option.id, lastCheckTime: this.option.contentState.checkedAt})
    //   }
    //   // this.updateContentStatus()
    //   this.checkOptionURL()
    // }
    if (this.isNewOption) {
      if (!this.option.url) {
        // New option with no URL to pull the values from, so open it for editing
        this.editCriteria()
      }
    }
    else { // Not a new option, so check the URL to update status
      if (this.option.contentState) {
        this.$store.commit(`decision/${DecisionTypes.MUTATION_UPDATE_OPTION_CHECKED_TIME}`, {id: this.option.id, lastCheckTime: this.option.contentState.checkedAt})
      }
      // this.updateContentStatus()
      this.checkOptionURL()
    }
    this.loadOptionReasons()
  },
  beforeUnmount: function () {
    // Clear any intervals that might still be going
    // if (this.checkScrapeFinishedInterval) {
    //   clearInterval(this.checkScrapeFinishedInterval)
    // }

    // if (this.checkRescrapeFinishedInterval) {
    //   clearInterval(this.checkRescrapeFinishedInterval)
    // }

    // if (this.checkContentChangedInterval) {
    //   clearInterval(this.checkContentChangedInterval)
    // }
  },
  /*watch: {
    // whenever sha256 changes, this function will run
    'option.sha256': function (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.checkOptionURL()
      }
    }
  },*/
  methods: {
    // updateContentStatus: function () {
    //   if (!this.option.contentState) {
    //     return
    //   }
    //
    //   if (this.option.contentState.broken) {
    //     this.lastScrapeStatus = this.urlStates.broken
    //   }
    //   else if (this.option.contentState.sha256 ) {
    //     if (this.option.contentState.sha256 === this.option.sha256) {
    //       this.lastScrapeStatus = this.urlStates.latest
    //     } else {
    //       this.lastScrapeStatus = this.urlStates.stale
    //     }
    //   }
    // },
    handleToggleCompareAction: function () {
      this.$emit('onCompare')
    },
    loadOptionReasons: async function () {
      this.optionReasons = []
      const token = firebase.auth().currentUser && (await firebase.auth().currentUser.getIdToken())
      const reasonsURL = `${Constants.API_BASE_PATH}/decisions/${this.option.decisionId}/options/${this.option.id}/reasons`
      const res = await axios.get(reasonsURL, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      });
      if (res.status === 200) {
        // console.log(`GOT reasons response ${JSON.stringify(res.data)}`)
        this.optionReasons = res.data
        // Also set it for the option
        this.option.optionReasons = this.optionReasons
      }
    },
    handleNewReason: async function (reasonObj) {
      reasonObj.owner = firebase.auth().currentUser.uid // Auther UID
      reasonObj.ownerName = firebase.auth().currentUser.displayName || firebase.auth().currentUser.email
      reasonObj.id = this.genGUID()
      this.optionReasons.push(reasonObj)
      // Save reason
      const token = firebase.auth().currentUser && (await firebase.auth().currentUser.getIdToken())
      const postReasonURL = `${Constants.API_BASE_PATH}/decisions/${this.option.decisionId}/options/${this.option.id}/reasons`
      const postBody = reasonObj

      // console.log(`Calling save option reason ${JSON.stringify(postBody)}`)
      const res = await axios.post(postReasonURL, postBody, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      })

      if (res && res.status === 200 && res.data && res.data.status === "success") {
        // console.log('Reason saved')
        // Update the ID to the one used to store the reason
        const newReasonIndex = this.optionReasons.findIndex(reason => reason.id === reasonObj.id)
        if (newReasonIndex > -1) {
          this.optionReasons[newReasonIndex].id = res.data.reasonId
        }
      } else {
        // console.log(`Unexpected response ${JSON.stringify(res)}`)
        // Remove the item from the reasons list
        const newReasonIndex = this.optionReasons.findIndex(reason => reason.id === reasonObj.id)
        if (newReasonIndex > -1) {
          this.optionReasons.splice(newReasonIndex, 1)
        }
      }
    },
    deleteReason: async function (reasonObj) {
      if (reasonObj.owner !== this.currentUserID) {
        return
      }

      const token = firebase.auth().currentUser && (await firebase.auth().currentUser.getIdToken())
      const deleteReasonURL = `${Constants.API_BASE_PATH}/decisions/${this.option.decisionId}/options/${this.option.id}/reasons/${reasonObj.id}`

      // console.log('Calling delete reason')
      reasonObj.deleting = true
      const res = await axios.delete(deleteReasonURL, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      })

      if (res && res.status === 200 && res.data && res.data.status === "success") {
        // Remove the item from the reasons list
        const deletedReasonIndex = this.optionReasons.findIndex(reason => reason.id === reasonObj.id)
        if (deletedReasonIndex > -1) {
          this.optionReasons.splice(deletedReasonIndex, 1)
        }
      } else {
        // console.log(`Unexpected response ${JSON.stringify(res)}`)
        reasonObj.deleting = false
        alert('Unexpected response deleting reason')
      }
    },
    editCriteria: function () {
      this.editingValues = JSON.parse(JSON.stringify(this.criteriaValues))
      this.editingCriteria = true
    },
    cancelEditCriteria: function () {
      this.editingValues = null
      this.editingCriteria = false
    },
    saveCriteria: async function () {
      // TODO introduce dirty state for criteria editing to prevent saving and triggering learning if there are no value changes
      const token = firebase.auth().currentUser && (await firebase.auth().currentUser.getIdToken())
      const postCriteriaValuesURL = `${Constants.API_BASE_PATH}/decisions/${this.option.decisionId}/options/${this.option.id}/criteria`
      const postBody = {criteriaValues: this.editingValues}

      // console.log(`Calling option post criteria ${JSON.stringify(postBody)}`)
      const res = await axios.post(postCriteriaValuesURL, postBody, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      })

      if (res && res.status === 200 && res.data && res.data.status === "success") {
        this.$store.commit(`decision/${DecisionTypes.MUTATION_OPTION_CRITERIA_VALUES}`, {id: this.option.id, criteriaValues:  JSON.parse(JSON.stringify(this.editingValues))})
        // this.option.criteriaValues =  JSON.parse(JSON.stringify(this.editingValues))
        this.editingCriteria = false
        // console.log('Saved criteria value changes. Emitting onCriteriaValues')
        this.$emit('onCriteriaValues')
      } else {
        // console.log(`Unexpected response ${JSON.stringify(res)}`)
        alert('Unexpected response updating criteria')
      }
    },
    deleteOption: async function () {
      if (this.option.shortListed) {
        return
      }

      const token = firebase.auth().currentUser && (await firebase.auth().currentUser.getIdToken())
      const deleteOptionURL = `${Constants.API_BASE_PATH}/decisions/${this.option.decisionId}/options/${this.option.id}`

      // console.log('Calling delete option')
      this.deleting = true
      const res = await axios.delete(deleteOptionURL, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      })

      if (res && res.status === 200 && res.data && res.data.status === "success") {
        // Emit so the main list can update
        this.$emit('onDelete')
        this.confirmDeleteDialog = false
        // this.deleting = false
      } else {
        // console.log(`Unexpected response ${JSON.stringify(res)}`)
        this.deleting = false
        alert('Unexpected response deleting criteria')
      }
    },
    checkOptionURL: async function () {
      // Only check url for approved options.
      if (this.option.scrapeState !== Constants.SCRAPE_STATES.approved) {
        return
      }
      const timeSinceListCheck = Math.abs(this.computedLastCheckTime - Date.now())
      // console.log(`Checking if it is time to check. computedLastCheckTime=${this.computedLastCheckTime} ${timeSinceListCheck}`)
      const timeToCheck = timeSinceListCheck > 7200000 /* 2 hours in milliseconds*/
      if (this.option.url && timeToCheck) {

        // this.lastScrapeStatus = this.urlStates.unknown

        // const token = firebase.auth().currentUser && (await firebase.auth().currentUser.getIdToken())
        const checkOptionURL = `${Constants.API_BASE_PATH}/decisions/${this.option.decisionId}/options/${this.option.id}/urlcheck`

        // this.checkingContent = true

        // If not, check content has not changed
        const res = await axios.post(checkOptionURL, {url: encodeURI(this.option.url), expireTimeMS: 7200000 /* 2 hours in milliseconds*/}, {
          headers: {
            'Content-Type': 'application/json',
            // 'Authorization': `Bearer ${token}`,
          }
        })

        if (res && res.status === 200 && res.data) {
          // if (res.data.triggeredRecheck) {
          //   // console.log(`A Hash recheck was triggered res= ${JSON.stringify(res.data)}`)
          //   // TODO update content state with last checked date
          //   this.checkingContentTimestamp = res.data.lastChecked
          //   this.$store.commit(`decision/${DecisionTypes.MUTATION_UPDATE_CONTENT_STATE}`, {id: this.option.id, contentState: res.data.contentState })
          //   // this.startCheckContentChangedFinished() // TODO remove this and handle differences in lastChecked date vs new check date in optionsList.
          // }
          // else {
          //   // console.log(`No hash recheck was triggered = ${JSON.stringify(res.data)}`)
          //   if (res.data.contentState) {
          //     this.$store.commit(`decision/${DecisionTypes.MUTATION_UPDATE_CONTENT_STATE}`, {id: this.option.id, contentState: res.data.contentState })
          //     this.$store.commit(`decision/${DecisionTypes.MUTATION_UPDATE_OPTION_CHECKED_TIME}`, {id: this.option.id, lastCheckTime: res.data.contentState})
          //     // this.updateContentStatus()
          //   }
          //
          //   if (res.data.broken) {
          //     // this.lastScrapeStatus = this.urlStates.broken
          //     // Update checked at so we do not recheck broken urls
          //     this.$store.commit(`decision/${DecisionTypes.MUTATION_UPDATE_OPTION_CHECKED_TIME}`, {id: this.option.id, lastCheckTime: this.option.contentState.checkedAt})
          //   }
          //   // this.checkingContent = false
          // }
        }
      }

      // if (res && res.status === 200 && res.data) {
      //   if (res.data.broken) {
      //     this.lastScrapeStatus = this.urlStates.broken
      //   }
      //   else if (res.data.sha256 ) {
      //     if (res.data.sha256 === this.option.sha256) {
      //       this.lastScrapeStatus = this.urlStates.latest
      //       this.$store.commit(`decision/${DecisionTypes.MUTATION_UPDATE_OPTION_CHECKED_TIME}`, {id: this.option.id})
      //     } else {
      //       this.lastScrapeStatus = this.urlStates.stale
      //     }
      //   }
      //   this.checkingContent = false
      // } else {
      //   // console.log(`Unexpected response ${JSON.stringify(res)}`)
      //   this.checkingContent = false
      // }
    },
    checkOutOfDateContent: async function () {
      if (this.urlStatus === this.urlStates.stale) {
        this.checkCriteriaValues() // Check all criteria values for changes when url is stale
      } else if (this.urlStatus === this.urlStates.changed) {
        this.reviewNewValues()
      }
    },
    triggerRescrapeOptionValue: async function (criteriaItemId) {
      const valueStatus = this.valueSyncIcons[criteriaItemId].status
      if (valueStatus === 'unscraped') {
        this.checkUnknownValue(criteriaItemId) // Check for new criteria values for unscraped criteria
      }
      else if (this.urlStatus === this.urlStates.changed && valueStatus === this.urlStates.changed) {
        this.reviewNewValues()
      }
      // console.log('Trigger rescrape optoin click')
    },
    reviewNewValues: function () {
      // Run full rescrape UI
      // console.log('TODO rescrape UI with scrapeResult')
      // this.$emit('onRequestReviewRescrape', this.option, this.checkedValuesScrapeResult)
      this.$emit('onRequestReviewRescrape', this.option, this.option.rescrapeResult)
    },
    reBuildOptionValues: function () {
      this.rebuilding = true
      this.$emit('onResetScrape', this.option)
    },
    reviewOptionCriteria: function () {
      this.$emit('onReview')
    },
    approveOptionCriteria: function () {
      this.$emit('onApproved', this.option)
    },
    // Use this method to scrape new criteria for an already scraped option.
    // It will call a single value scrape and start an interval to check when it is finished.
    // The function called by the interval 'checkScrapingFinished' will transform the scrape data into data appropriate for the RescrapeCriteriaDialog.
    checkUnknownValue: async function (criteriaId) {
      const userUID = firebase.auth().currentUser ? firebase.auth().currentUser.uid : null;
      const token = firebase.auth().currentUser && (await firebase.auth().currentUser.getIdToken())
      const postResetScrapeStateURL = `${Constants.API_BASE_PATH}/decisions/${this.option.decisionId}/options/${this.option.id}/criteria/scrape`
      const scrapeCriteriaList = this.criteria.filter(criteriaItem => criteriaItem.id === criteriaId)

      const valuesOnlyScrapeBody = {
        url: encodeURI(this.option.url),
        criteria: scrapeCriteriaList,
        valuesOnlyScrape: true
      }
      if (userUID) {
        valuesOnlyScrapeBody.uid = userUID // used to find user specific synonyms
      }

      // this.checkingValues = true
      this.checkingValuesImmediate = true
      setTimeout(() => { this.checkingValuesImmediate = false }, 2000)

      // const res = await axios.post(postResetScrapeStateURL, valuesOnlyScrapeBody, {
      await axios.post(postResetScrapeStateURL, valuesOnlyScrapeBody, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      })

      this.$store.commit(`decision/${DecisionTypes.MUTATION_OPTION_SCRAPE_STATE}`, {id: this.option.id, scrapeState: Constants.SCRAPE_STATES.scraping})

      // if (res && res.status === 200 && !this.checkScrapeFinishedInterval) {
      //   this.startCheckScrapingFinished()
      // }

    },
    checkCriteriaValues: async function () {
      // Wave a two phase rescprape. Check values here and show if there are any that have changed.
      // Then launch a new scraping UI if the the values are out of sync
      const token = firebase.auth().currentUser && (await firebase.auth().currentUser.getIdToken())
      const checkValuesURL = `${Constants.API_BASE_PATH}/decisions/${this.option.decisionId}/options/${this.option.id}/criteria/checkvalues`

      const bodyCriteriaValuesList = []
      // Build list of...
      //  criteriaId: string,
      //  selectorId: string,
      //  selectorKey: string,
      //  sourceValue: string
      const criteriaIds = Object.keys(this.criteriaValues)
      criteriaIds.forEach(criteriaKey => {
        const valueItem = this.criteriaValues[criteriaKey]
        const criteriaItem = this.criteria.find(next => next.id === criteriaKey)
        if (valueItem.sourceValue && !criteriaItem.ignoreInScrape) {
          bodyCriteriaValuesList.push({
            criteriaId: criteriaKey,
            selectorId: valueItem.selectorId,
            selectorKey: valueItem.selectorKey,
            sourceValue: valueItem.sourceValue,
            keyword: valueItem.keyword,
            filteredKeywordFromValue: valueItem.filteredKeywordFromValue,
            keywordOnly: criteriaItem && criteriaItem.keywordOnly
          })
        }
      })

      const checkValuesBody = {
        url: encodeURI(this.option.url),
        criteriaValues: bodyCriteriaValuesList
      }

      // console.log(`Calling check values post ${JSON.stringify(checkValuesBody)}`)
      // this.checkedValuesScrapeResult = null
      // this.checkingValues = true
      // let foundValueDifferences = false
      this.checkingValuesImmediate = true
      setTimeout(() => { this.checkingValuesImmediate = false }, 2000)

      // const res = await axios.post(checkValuesURL, checkValuesBody, {
      await axios.post(checkValuesURL, checkValuesBody, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      })

      this.$store.commit(`decision/${DecisionTypes.MUTATION_OPTION_SCRAPE_STATE}`, {id: this.option.id, scrapeState: Constants.SCRAPE_STATES.validating})

      // if (res && res.status === 200 && !this.checkRescrapeFinishedInterval) {
      //   this.startCheckRescrapingFinished()
      // }

      // let newSha256 = null
      // if (res && res.status === 200 && res.data && res.data.scrapeResult && res.data.scrapeResult.status !== 'failed') {
      //   this.checkedValuesScrapeResult = res.data.scrapeResult
      //   // console.log(`Got a scrape response ${JSON.stringify(res.data)}`)
      //   newSha256 = this.checkedValuesScrapeResult.sha256
      //
      //   const newValues = this.checkedValuesScrapeResult.newValues || null
      //   if (newValues && !(Object.keys(newValues).length === 0 && newValues.constructor === Object)) {
      //     foundValueDifferences = true
      //   }
      //
      // }

      // this.checkingValues = false
      //
      // // Update option sha256 if none have changed, otherwise do not.
      //
      // if (foundValueDifferences) {
      //   this.lastScrapeStatus = this.urlStates.changed
      // } else if (newSha256 && this.option.sha256 !== newSha256) {
      //   this.updatingSha256 = true
      //   // Save new sha256
      //   const postNewSha256URL = `${Constants.API_BASE_PATH}/decisions/${this.option.decisionId}/options/${this.option.id}/sha256`
      //   const postSha256Body = {sha256: newSha256}
      //   // console.log(`Calling option post sha256 ${JSON.stringify(postSha256Body)}`)
      //   await axios.post(postNewSha256URL, postSha256Body, {
      //     headers: {
      //       'Content-Type': 'application/json',
      //       'Authorization': `Bearer ${token}`,
      //     }
      //   })
      //
      //   this.option.sha256 = newSha256
      //   this.lastScrapeStatus = this.urlStates.latest
      //   this.updatingSha256 = false
      // }
    },
    // startCheckScrapingFinished: function () {
    //   // if (!this.checkingValues) {
    //   //   this.checkingValues = true
    //   // }
    //   // repeat with the interval of 4 seconds
    //   this.checkScrapeFinishedInterval = setInterval(() => this.checkScrapingFinished(), 4000)
    // },
    // This method will fetch the scrape state of the option.
    // If it is set to 'scraped' it will fetch the scrape result and transform the data to a new values list
    // as expected by the rescrape criteria dialog.
    // This is used by single value rescrape
    // checkScrapingFinished: async function () {
    //   const token = firebase.auth().currentUser && (await firebase.auth().currentUser.getIdToken())
    //   const checkOptionScrapeState = `${Constants.API_BASE_PATH}/decisions/${this.option.decisionId}/options/${this.option.id}/scrapestate`
    //
    //   // If not, check content has not changed
    //   const res = await axios.get(checkOptionScrapeState, {
    //     headers: {
    //       'Authorization': `Bearer ${token}`,
    //     }
    //   })
    //
    //   if (res && res.status === 200 && res.data && res.data.state === Constants.SCRAPE_STATES.scraped) {
    //     clearInterval(this.checkScrapeFinishedInterval)
    //
    //     this.$store.commit(`decision/${DecisionTypes.MUTATION_OPTION_SCRAPE_STATE}`, {id: this.option.id, scrapeState: res.data.state})
    //
    //     // Load the latest option data
    //     const getOptionURL = `${Constants.API_BASE_PATH}/decisions/${this.option.decisionId}/options/${this.option.id}`
    //     // console.log(`Calling decision get optoin`)
    //     const optionRes = await axios.get(getOptionURL, {
    //       headers: {
    //         'Content-Type': 'application/json',
    //         'Authorization': `Bearer ${token}`,
    //       }
    //     })
    //     // console.log(`Status is approved, got option data`)
    //     if (optionRes && optionRes.status === 200 && optionRes.data && optionRes.data.status === 'success' ) {
    //       const newOptionData = optionRes.data.optionData
    //
    //
    //       // this.lastScrapeStatus = this.urlStates.unknown
    //       const rescrapeResult = {
    //         url: newOptionData.url,
    //         title: newOptionData.name,
    //         sha256: newOptionData.sha256,
    //         imageOptions: [{url: newOptionData.imgSrc, ranking: 1, position: 1}]
    //       }
    //
    //       this.$store.commit(`decision/${DecisionTypes.MUTATION_OPTION_SCRAPE_DATA}`, {
    //         id: this.option.id,
    //         name: newOptionData.name,
    //         imgSrc: newOptionData.imgSrc,
    //         sha256: newOptionData.sha256,
    //         scrapeResult: rescrapeResult,
    //         criteriaValues: newOptionData.criteriaValues,
    //         criteriaValueOptions: newOptionData.criteriaValueOptions,
    //         scrapeState: Constants.SCRAPE_STATES.scraped
    //       })
    //
    //       // Object.keys(newOptionData.criteriaValueOptions).forEach(key => {
    //       //   rescrapeResult.newValues[key] = newOptionData.criteriaValueOptions[key]
    //       // })
    //
    //       // this.checkedValuesScrapeResult = rescrapeResult
    //
    //
    //       // const newSha256 = rescrapeResult.sha256
    //       // const newValues = rescrapeResult.newValues || null
    //       // console.log(`New Values = ${JSON.stringify(newValues)}`)
    //       // let foundValueDifferences = false
    //       //
    //       // if (newValues && !(Object.keys(newValues).length === 0 && newValues.constructor === Object)) {
    //       //   foundValueDifferences = true
    //       // }
    //
    //       // Update option sha256 if none have changed, otherwise do not.
    //       // if (foundValueDifferences) {
    //       //   this.lastScrapeStatus = this.urlStates.unknown
    //       // }
    //       // else if (newSha256 && this.option.sha256 !== newSha256) {
    //       //   this.updatingSha256 = true
    //       //   // Save new sha256
    //       //   const postNewSha256URL = `${Constants.API_BASE_PATH}/decisions/${this.option.decisionId}/options/${this.option.id}/sha256`
    //       //   const postSha256Body = {sha256: newSha256}
    //       //   // console.log(`Calling option post sha256 ${JSON.stringify(postSha256Body)}`)
    //       //   await axios.post(postNewSha256URL, postSha256Body, {
    //       //     headers: {
    //       //       'Content-Type': 'application/json',
    //       //       'Authorization': `Bearer ${token}`,
    //       //     }
    //       //   })
    //       //
    //       //   this.$store.commit(`decision/${DecisionTypes.MUTATION_OPTION_HASH}`, {id: this.option.id, sha256: newSha256})
    //       //   // this.option.sha256 = newSha256
    //       //   this.lastScrapeStatus = this.urlStates.latest
    //       //   this.updatingSha256 = false
    //       // }
    //
    //     }
    //
    //     this.checkScrapeFinishedInterval = null
    //     // this.checkingValues = false
    //   }
    // },
    // checkScrapingFinished: async function () {
    //   const token = firebase.auth().currentUser && (await firebase.auth().currentUser.getIdToken())
    //   const checkOptionScrapeState = `${Constants.API_BASE_PATH}/decisions/${this.option.decisionId}/options/${this.option.id}/scrapestate`
    //
    //   // If not, check content has not changed
    //   const res = await axios.get(checkOptionScrapeState, {
    //     headers: {
    //       'Authorization': `Bearer ${token}`,
    //     }
    //   })
    //
    //   if (res && res.status === 200 && res.data && res.data.state === Constants.SCRAPE_STATES.scraped /* Scraped state */) {
    //     clearInterval(this.checkScrapeFinishedInterval)
    //
    //     // Load the latest option data
    //     const getOptionURL = `${Constants.API_BASE_PATH}/decisions/${this.option.decisionId}/options/${this.option.id}`
    //     // console.log(`Calling decision get optoin`)
    //     const optionRes = await axios.get(getOptionURL, {
    //       headers: {
    //         'Content-Type': 'application/json',
    //         'Authorization': `Bearer ${token}`,
    //       }
    //     })
    //     // console.log(`Status is approved, got option data`)
    //     if (optionRes && optionRes.status === 200 && optionRes.data && optionRes.data.status === 'success' ) {
    //       const newOptionData = optionRes.data.optionData
    //       // Update option model and change the scrape state to approved
    //       const postNewStateURL = `${Constants.API_BASE_PATH}/decisions/${this.option.decisionId}/options/${this.option.id}/scrapestate`
    //       const postStateBody = {scrapeState: Constants.SCRAPE_STATES.approved}
    //       // console.log(`Calling option post sha256 ${JSON.stringify(postSha256Body)}`)
    //       const changeStateRes = await axios.post(postNewStateURL, postStateBody, {
    //         headers: {
    //           'Content-Type': 'application/json',
    //           'Authorization': `Bearer ${token}`,
    //         }
    //       })
    //
    //       // TODO transform the scrapeResult to a rescrapeResult
    //       if (changeStateRes && changeStateRes.status === 200) {
    //         this.$store.commit(`decision/${DecisionTypes.MUTATION_OPTION_SCRAPE_STATE}`, {id: this.option.id, scrapeState: Constants.SCRAPE_STATES.approved})
    //         const rescrapeResult = {
    //           newValues: {},
    //           // screenGrabId: newOptionData.scrapeResult.screenGrabId,
    //           sha256: newOptionData.sha256,
    //           url: newOptionData.url
    //         }
    //         Object.keys(newOptionData.criteriaValueOptions).forEach(key => {
    //           rescrapeResult.newValues[key] = newOptionData.criteriaValueOptions[key]
    //         })
    //
    //         this.checkedValuesScrapeResult = rescrapeResult
    //
    //
    //         const newSha256 = this.checkedValuesScrapeResult.sha256
    //         const newValues = this.checkedValuesScrapeResult.newValues || null
    //         // console.log(`New Values = ${JSON.stringify(newValues)}`)
    //         let foundValueDifferences = false
    //
    //         if (newValues && !(Object.keys(newValues).length === 0 && newValues.constructor === Object)) {
    //           foundValueDifferences = true
    //         }
    //
    //         // Update option sha256 if none have changed, otherwise do not.
    //         if (foundValueDifferences) {
    //           this.lastScrapeStatus = this.urlStates.changed
    //         } else if (newSha256 && this.option.sha256 !== newSha256) {
    //           this.updatingSha256 = true
    //           // Save new sha256
    //           const postNewSha256URL = `${Constants.API_BASE_PATH}/decisions/${this.option.decisionId}/options/${this.option.id}/sha256`
    //           const postSha256Body = {sha256: newSha256}
    //           // console.log(`Calling option post sha256 ${JSON.stringify(postSha256Body)}`)
    //           await axios.post(postNewSha256URL, postSha256Body, {
    //             headers: {
    //               'Content-Type': 'application/json',
    //               'Authorization': `Bearer ${token}`,
    //             }
    //           })
    //
    //           this.$store.commit(`decision/${DecisionTypes.MUTATION_OPTION_HASH}`, {id: this.option.id, sha256: newSha256})
    //           // this.option.sha256 = newSha256
    //           this.lastScrapeStatus = this.urlStates.latest
    //           this.updatingSha256 = false
    //         }
    //       }
    //
    //     }
    //
    //     this.checkScrapeFinishedInterval = null
    //     this.checkingValues = false
    //   }
    // },
    // startCheckRescrapingFinished: function () {
    //   // if (!this.checkingValues) {
    //   //   this.checkingValues = true
    //   // }
    //   // repeat with the interval of 4 seconds
    //   this.checkRescrapeFinishedInterval = setInterval(() => this.checkRescrapingFinished(), 4000)
    // },
    // checkRescrapingFinished: async function () {
    //   const token = firebase.auth().currentUser && (await firebase.auth().currentUser.getIdToken())
    //   const checkOptionScrapeState = `${Constants.API_BASE_PATH}/decisions/${this.option.decisionId}/options/${this.option.id}/scrapestate`
    //
    //   // If not, check content has not changed
    //   const res = await axios.get(checkOptionScrapeState, {
    //     headers: {
    //       'Authorization': `Bearer ${token}`,
    //     }
    //   })
    //
    //   if (res && res.status === 200 && res.data && res.data.state === Constants.SCRAPE_STATES.approved /* Approved state */) {
    //     clearInterval(this.checkRescrapeFinishedInterval)
    //
    //     // Load the latest option data
    //     const getOptionURL = `${Constants.API_BASE_PATH}/decisions/${this.option.decisionId}/options/${this.option.id}`
    //     // console.log(`Calling decision get optoin`)
    //     const optionRes = await axios.get(getOptionURL, {
    //       headers: {
    //         'Content-Type': 'application/json',
    //         'Authorization': `Bearer ${token}`,
    //       }
    //     })
    //     // console.log(`Status is approved, got option data`)
    //     if (optionRes && optionRes.status === 200 && optionRes.data && optionRes.data.status === 'success' ) {
    //       const newOptionData = optionRes.data.optionData
    //       // Update local option model
    //       this.$store.commit(`decision/${DecisionTypes.MUTATION_OPTION_SCRAPE_STATE}`, {id: this.option.id, scrapeState: newOptionData.scrapeState})
    //       this.$store.commit(`decision/${DecisionTypes.MUTATION_UPDATE_CONTENT_STATE}`, {id: this.option.id, contentState: newOptionData.contentState})
    //       // this.option.scrapeState = newOptionData.scrapeState
    //       this.checkedValuesScrapeResult = newOptionData.rescrapeResult
    //       const newSha256 = this.checkedValuesScrapeResult.sha256
    //       const newValues = this.checkedValuesScrapeResult.newValues || null
    //       // console.log(`New Values = ${JSON.stringify(newValues)}`)
    //       let foundValueDifferences = false
    //
    //       if (newValues && !(Object.keys(newValues).length === 0 && newValues.constructor === Object)) {
    //         foundValueDifferences = true
    //       }
    //
    //       // Update option sha256 if none have changed, otherwise do not.
    //       if (foundValueDifferences) {
    //         // this.lastScrapeStatus = this.urlStates.changed
    //       } else if (newSha256 && this.option.sha256 !== newSha256) {
    //         this.updatingSha256 = true
    //         // Save new sha256
    //         const postNewSha256URL = `${Constants.API_BASE_PATH}/decisions/${this.option.decisionId}/options/${this.option.id}/sha256`
    //         const postSha256Body = {sha256: newSha256}
    //         // console.log(`Calling option post sha256 ${JSON.stringify(postSha256Body)}`)
    //         await axios.post(postNewSha256URL, postSha256Body, {
    //           headers: {
    //             'Content-Type': 'application/json',
    //             'Authorization': `Bearer ${token}`,
    //           }
    //         })
    //
    //         this.$store.commit(`decision/${DecisionTypes.MUTATION_OPTION_HASH}`, {id: this.option.id, sha256: newSha256})
    //         this.$store.commit(`decision/${DecisionTypes.MUTATION_UPDATE_OPTION_CHECKED_TIME}`, {id: this.option.id, lastCheckTime: Date.now()})
    //         // this.option.sha256 = newSha256
    //         // this.lastScrapeStatus = this.urlStates.latest
    //         this.updatingSha256 = false
    //       }
    //
    //     }
    //
    //     this.checkRescrapeFinishedInterval = null
    //     // this.checkingValues = false
    //   }
    // },

    // startCheckContentChangedFinished: function () {
    //   if (!this.checkingContent) {
    //     this.checkingContent = true
    //   }
    //   // console.log('Starting interval to check if the contents changed')
    //   // repeat with the interval of 3 seconds
    //   this.checkContentChangedInterval = setInterval(() => this.checkContentChangedFinished(), 3000)
    //
    //   // after 9 seconds stop regardles
    //   const self = this
    //   setTimeout(() => { if (this.checkContentChangedInterval) {clearInterval(self.checkContentChangedInterval); self.checkingContent = false; self.checkContentChangedInterval = null;} }, 90000)
    // },
    // checkContentChangedFinished: async function () {
    //   const token = firebase.auth().currentUser && (await firebase.auth().currentUser.getIdToken())
    //   const checkOptionContentStateURL = `${Constants.API_BASE_PATH}/decisions/${this.option.decisionId}/options/${this.option.id}/contentstate`
    //
    //   // If not, check content has not changed
    //   const res = await axios.get(checkOptionContentStateURL, {
    //     headers: {
    //       'Authorization': `Bearer ${token}`,
    //     }
    //   })
    //   // console.log(`Got a response for latest content state ${JSON.stringify(res.data)}`)
    //   if (res && res.status === 200 && res.data && res.data.checkedAt > this.checkingContentTimestamp) {
    //     clearInterval(this.checkContentChangedInterval)
    //
    //
    //     this.$store.commit(`decision/${DecisionTypes.MUTATION_UPDATE_CONTENT_STATE}`, {id: this.option.id, contentState: res.data})
    //     this.updateContentStatus()
    //
    //     this.$store.commit(`decision/${DecisionTypes.MUTATION_UPDATE_OPTION_CHECKED_TIME}`, {id: this.option.id, lastCheckTime: this.option.contentState.checkedAt})
    //     this.checkingContent = false
    //     this.checkContentChangedInterval = null
    //   }
    // },
    openURL: function (url) {
      window.open(url, '_blank');
    },
    updateCriteriaValues: function () {
      this.criteria.forEach((item) => {
        if (!this.option.criteriaValues[item.id]) {
          this.option.criteriaValues[item.id] = {userValue: null, sourceValue: null}
        }
      })
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.deleting {
  opacity: 0.2;
}
.button-info-text {
  color: #DDD;
  font-size: 0.7em;
}
.v-card__title {
  font-size: 1rem;
  position: relative;
  background-color: rgba(0,0,0,0.6);
  // background-color: rgba(255,255,255,0.8);
  padding: 5px 16px;
  border-top: 1px solid #000;
  border-bottom: 1px solid #CCC;
}
.top-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .non-breaking-space {
    width: 28px;
  }

  .compare-active {
    background-color: #4caf50;
    color: #FFF;

    &:hover {
      background-color: rgba(76, 175, 80, 0.3);
      color: #607d8b;
    }
  }
}
.delete-action:hover {
  color: #FFF;
  background-color: rgba(244, 67, 54, 0.7);
}
.user-name-caption {
  font-size: 0.65rem;
  position: absolute;
  top: -1px;
}
</style>
<style lang="scss">
.not-found {
  .option-img .v-image__image {
    opacity: 0.2;
  }
  .criteria-section {
    opacity: 0.6;
  }
}
.editing-value-field.with-value .v-label {
  color: #4caf50;
  opacity: 1;
}
.editing-value-field.no-value .v-label {
  color: #f44336;
  opacity: 1;
}
.d-option-card-name {
  // color: #000;
  // background-color: #FFF;
  .edit-card-name {
    display: none;
  }

  &:hover {
    .edit-card-name {
      display: block;
    }
  }
}
</style>
